import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, inject, input } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';
import { MarkdownModule } from 'ngx-markdown';

import { AlertService } from '@valk-nx/components/ui-alert/src/lib/services/alert.service';
import { form as UINewsletterForm } from '@valk-nx/components/ui-newsletter/src/lib/form';
import { NewsletterComponent as UINewsletter } from '@valk-nx/components/ui-newsletter/src/lib/newsletter';
import { RECAPTCHA_V2_SITE_KEY } from '@valk-nx/core/lib/core';
import {
	NewsletterService,
	NewsletterType,
} from '@valk-nx/services/newsletter/src/lib/newsletter.service';
import { HeadingGroupComponent } from '@valk-nx/storyblok/components/heading-group/src/lib/heading-group';
import { StoryblokDirective } from '@valk-nx/storyblok-directives/src/lib/directives/storyblok.directive';
import { StoryblokRootDirective } from '@valk-nx/storyblok-directives/src/lib/directives/storyblok-root.directive';
import { alertErrorSettings } from '@valk-nx/storyblok-services/src/lib/globals';
import { TagManagerFacade } from '@valk-nx/storyblok-store/src/lib/store/tag-manager/tag-manager.facade';

@Component({
	selector: 'sb-newsletter',
	templateUrl: './newsletter.html',
	imports: [
		UINewsletter,
		HeadingGroupComponent,
		MarkdownModule,
		StoryblokDirective,
	],
})
export class NewsletterComponent extends StoryblokRootDirective {
	private readonly cd = inject(ChangeDetectorRef);
	private readonly translate = inject(TranslateService);
	private readonly newsletterService = inject(NewsletterService);
	private readonly alertService = inject(AlertService);
	private readonly tagManagerFacade = inject(TagManagerFacade);
	readonly recaptchaV2Key = inject(RECAPTCHA_V2_SITE_KEY);

	title = input.required<string>();
	overline = input<string>('');
	newsletterType = input<NewsletterType>('leisure');

	newsletterSubmitting = false;
	newsletterSubmitError = true;
	newsletterSubmitted = false;
	recaptchaVersion: 'v2' | 'v3' = 'v3';
	newsletterForm = UINewsletterForm;

	public onSubmit([emailAddress, recaptchaV2Token]: [
		string,
		string?,
	]): Promise<void> {
		this.newsletterSubmitting = true;
		this.newsletterSubmitError = false;

		return this.newsletterService
			.newsletterSubscribe(
				emailAddress,
				this.newsletterType(),
				recaptchaV2Token,
			)
			.then(() => {
				this.tagManagerFacade.newsletterSubmit(emailAddress);
				this.newsletterSubmitted = true;
			})
			.catch((error: HttpErrorResponse) => {
				if (this.recaptchaVersion === 'v2') {
					this.alertService.error({
						...alertErrorSettings,
						content: this.translate.instant('global.service.error'),
					});

					this.newsletterSubmitError = true;
					this.newsletterSubmitted = false;
				} else {
					this.useRecaptchaV2(error.status);
				}
			})
			.finally(() => {
				this.newsletterSubmitting = false;
				this.cd.detectChanges();
			});
	}

	useRecaptchaV2(status: number | undefined) {
		if (
			status === 403 &&
			this.recaptchaVersion === 'v3' &&
			this.recaptchaV2Key
		) {
			this.recaptchaVersion = 'v2';
			this.newsletterForm = [
				...this.newsletterForm,
				{
					id: 'recaptcha',
					key: 'recaptcha',
					props: {
						errorMode: 'handled',
						required: true,
						siteKey: this.recaptchaV2Key,
						size: 'default',
					},
					type: 'recaptchaV2',
				},
			];
		}
	}
}
